import { z } from "zod";

import { LANGUAGES } from "$src/core/services/search-classification/constants";
import type {
  Category,
  ClassificationDefinition,
  ClassificationResult,
  FacetKind,
  Hit,
  LanguageCode,
  ResolveTerms,
  Term,
} from "$src/core/services/search-classification/types";

const MAX_CANDIDATES = 48;

const FILLER_WORDS = new Set([
  "a",
  "an",
  "the",
  "or",
  "and",
  "to",
  "with",
  "no",
  "for",
  "of",
  "in",
  "about",
  "but",
]);

export const isLanguageSupported = (
  locale: string | undefined,
): locale is LanguageCode => {
  const base = locale?.split("-")[0]?.toLowerCase();
  return !!base && (LANGUAGES as readonly string[]).includes(base);
};

export const tagTerm = (label: string): string =>
  label
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)
    .join(" ");

export const tagsFor = ({
  categories,
}: ClassificationDefinition): { kind: FacetKind; label: string }[] =>
  categories.map((label) => ({ kind: "category" as const, label }));

export const buildMatchInstructions = (
  definition: ClassificationDefinition,
): string =>
  [
    "Match a media search query to category tags from this list. Return only tags that appear in the list.",
    "",
    `Categories: ${definition.categories.join(", ")}`,
    "",
    "Rules:",
    "- Include every genre that fits — not only the closest one, but each genre a theme commonly belongs to (aliens/space → Sci-Fi & Fantasy + Action & Adventure; dragon → also Family & Animation; heartbreak love → Romance + Drama). When a genre plausibly fits, include it.",
    '- A genre word — bare, plural, or misspelled ("Comedy", "comedies", "horrer") — or a simple request ("I want a comedy") is a positive request: return that genre.',
    "- Return [] only when no genre is named to keep: a title, person/place, off-topic question, or nonsense.",
    '- Negation removes a named genre, never adds one: "comedy but not romance" → Comedy; "not romance", "no drama" → [].',
    '- "psychological" → Drama, not Horror. Never invent tags.',
  ].join("\n");

export const buildMatchSchema = (definition: ClassificationDefinition) =>
  z.object({
    categories: z.array(
      z.enum(definition.categories as [Category, ...Category[]]),
    ),
  });

const tokenize = (query: string): Term[] =>
  query
    .split(/[^\p{L}\p{N}]+/u)
    .filter((text) => text.length > 0)
    .map((text) => ({ text, lower: text.toLowerCase(), consumed: false }));

const forEachWindow = (
  terms: Term[],
  maxWords: number,
  match: (text: string) => boolean,
): void => {
  for (let length = maxWords; length >= 1; length--) {
    for (let start = 0; start + length <= terms.length; start++) {
      const window = terms.slice(start, start + length);
      if (window.some((term) => term.consumed)) continue;

      if (match(window.map((term) => term.lower).join(" "))) {
        for (const term of window) term.consumed = true;
      }
    }
  }
};

// Longest window we need to generate: the longest tag name.
const maxCandidateWords = (definition: ClassificationDefinition): number => {
  let longest = 1;
  for (const { label } of tagsFor(definition)) {
    longest = Math.max(longest, tagTerm(label).split(" ").length);
  }
  return longest;
};

// No `kind` filter: `FacetKind` has a single member today, so every hit is a category. Reinstate
// filtering here (and pass the kind back in) the moment a second facet kind exists.
const pickLabels = <T extends string>(hits: Hit[]): T[] => {
  const bestByLabel = new Map<string, Hit>();

  for (const hit of hits) {
    const previous = bestByLabel.get(hit.label);
    if (!previous || hit.score > previous.score) {
      bestByLabel.set(hit.label, hit);
    }
  }

  return [...bestByLabel.values()]
    .sort((a, b) => b.score - a.score)
    .map((hit) => hit.label as T);
};

const candidatesFrom = (terms: Term[], maxWords: number): string[] => {
  const candidates = new Set<string>();

  for (let length = 1; length <= maxWords; length++) {
    for (let start = 0; start + length <= terms.length; start++) {
      const window = terms.slice(start, start + length);
      const single = window.length === 1 ? window[0] : undefined;
      if (single && FILLER_WORDS.has(single.lower)) continue;
      candidates.add(window.map((term) => term.lower).join(" "));
    }
  }

  return [...candidates].slice(0, MAX_CANDIDATES);
};

export const classifyQuery = async (
  query: string,
  definition: ClassificationDefinition,
  resolveTerms?: ResolveTerms,
): Promise<ClassificationResult | null> => {
  const terms = tokenize(query);
  // Nothing to classify is a successful empty match, not a failure.
  if (terms.length === 0) return { categories: [] };

  const hits: Hit[] = [];

  const maxWords = maxCandidateWords(definition);
  const candidates = candidatesFrom(terms, maxWords);

  // The whole query as one candidate. all-MiniLM is a sentence embedder, so a
  // lone token is off-distribution; embedding the full phrase is what reaches
  // the conversational rows. Static backends resolve it to nothing (it is not a
  // verbatim tag), so this is a no-op there.
  const wholeQuery = terms.map((term) => term.lower).join(" ");
  const withWhole = candidates.includes(wholeQuery)
    ? candidates
    : [...candidates, wholeQuery];

  if (resolveTerms && withWhole.length > 0) {
    const resolved = await resolveTerms(withWhole);

    forEachWindow(terms, maxWords, (text) => {
      const matched = resolved.get(text);
      if (!matched?.length) return false;
      hits.push(...matched);
      return true;
    });

    const wholeHits = resolved.get(wholeQuery);
    if (wholeHits?.length) hits.push(...wholeHits);
  }

  // Zero matches is a successful empty result, not a failure — the caller must
  // not fall back to another engine on it.
  const categories = pickLabels<Category>(hits);
  return { categories };
};
